import * as uuid from 'uuid';

import { type Aircraft } from '../Aircrafts/types';
import { type AircraftCountableType, type CountableChange, type CountableChangeType } from './types';
import { COUNTABLE_BEHAVIOR_INDETERMINATE, COUNTABLE_CHANGE_CORRECTION, getCountableTypeBehavior } from './types';

export const setConsumption = (
    countableChange: CountableChange,
    changeType: AircraftCountableType,
    changeValue: number | null
): CountableChange => {
    const stateIndex = countableChange.countableTypes.indexOf(changeType);
    const changeTypes = [...countableChange.changeTypes];
    const changeIndex = changeTypes.indexOf(changeType);
    const changeValues = [...countableChange.changeValues];
    if (changeIndex >= 0) {
        if (changeValue === null) {
            changeValues.splice(changeIndex, 1);
            changeTypes.splice(changeIndex, 1);
        } else {
            changeValues[changeIndex] = changeValue;
        }
    } else if (changeValue !== null) {
        changeValues.push(changeValue);
        changeTypes.push(changeType);
    }
    const countableValuesAfter = [...countableChange.countableValuesAfter];
    if (stateIndex >= 0) {
        countableValuesAfter[stateIndex] = countableChange.countableValuesBefore[stateIndex] + (changeValue || 0);
    }

    return { ...countableChange, changeTypes, changeValues, countableValuesAfter };
};

/** Changes the "before" value keeping an entered "after" value (what the pilot reads after the leg), so the change follows. */
export const setCountableBefore = (
    countableChange: CountableChange,
    countableType: AircraftCountableType,
    before: number
): CountableChange => {
    const stateIndex = countableChange.countableTypes.indexOf(countableType);
    if (stateIndex < 0) {
        return countableChange;
    }
    const countableValuesBefore = [...countableChange.countableValuesBefore];
    countableValuesBefore[stateIndex] = before;
    const changeValues = [...countableChange.changeValues];
    const countableValuesAfter = [...countableChange.countableValuesAfter];
    const changeIndex = countableChange.changeTypes.indexOf(countableType);
    if (changeIndex >= 0) {
        changeValues[changeIndex] = countableValuesAfter[stateIndex] - before;
    } else {
        // no change entered yet - the after value just mirrors the before value (as setConsumption(null) leaves it)
        countableValuesAfter[stateIndex] = before;
    }

    return { ...countableChange, countableValuesBefore, countableValuesAfter, changeValues };
};

/**
 * Copies the uplift's fluid after-values (fuel in any unit, oil, TKS, oxygen...) into the consumption's
 * before-values, in place. Totals and meters are left to the continuity logic.
 */
export const syncFluidsFromUplift = (uplift: CountableChange, consumed: CountableChange): void => {
    consumed.countableTypes.forEach((type, index) => {
        const upliftIndex = uplift.countableTypes.indexOf(type);
        if (upliftIndex >= 0 && getCountableTypeBehavior(type) === COUNTABLE_BEHAVIOR_INDETERMINATE) {
            consumed.countableValuesBefore[index] = uplift.countableValuesAfter[upliftIndex];
        }
    });
};

export const getInitialCountableChange = (aircraft: Aircraft, editableByUserIds?: string[]): CountableChange => {
    const zeroes = Array.from<number>({ length: aircraft.countableTypes.length }).fill(0);

    return {
        id: uuid.v4(),
        editableByUserIds: editableByUserIds || [],
        countableTypes: aircraft.countableTypes,
        countableValuesBefore: zeroes,
        countableValuesAfter: zeroes,
        changeValues: [],
        changeTypes: [],
        aircraftId: aircraft.id,
        createdAt: new Date(),
        createdByName: '(system initial status)',
        type: COUNTABLE_CHANGE_CORRECTION,
        deleted: false,
    };
};

type GetDerivedCountableChangeParams = {
    type: CountableChangeType;
    appliedByName: string;
    place: string;
    flightId?: string;
    legId?: string;
};

export const getDerivedCountableChange = (
    lastCountableChange: CountableChange,
    params: GetDerivedCountableChangeParams,
    editableByUserIds?: string[]
): CountableChange => ({
    id: uuid.v4(),
    type: params.type,
    createdByName: params.appliedByName,
    createdAt: new Date(),
    aircraftId: lastCountableChange.aircraftId,
    place: params.place,
    flightId: params.flightId,
    legId: params.legId,
    changeTypes: [],
    changeValues: [],
    countableValuesBefore: [...lastCountableChange.countableValuesAfter],
    countableTypes: [...lastCountableChange.countableTypes],
    countableValuesAfter: [...lastCountableChange.countableValuesAfter],
    deleted: false,
    editableByUserIds: editableByUserIds || [],
});

export const getCountableBeforeAfter = (
    counableChange: CountableChange,
    type: AircraftCountableType
): [null, null, null] | [number, number, number] => {
    const index = counableChange.countableTypes.indexOf(type);
    if (index >= 0) {
        const before = counableChange.countableValuesBefore[index];
        const after = counableChange.countableValuesAfter[index];

        return [before, after, index];
    }

    return [null, null, null];
};

export const getCountableChangeTypeHuman = (type: CountableChangeType) => {
    switch (type) {
        case 'consumption': {
            return 'consumption after flight';
        }
        case 'correction': {
            return 'correction';
        }
        case 'uplift': {
            return 'uplift';
        }
    }
};

export const areCountableChangesCompatible = (cc1: CountableChange, cc2: CountableChange) =>
    cc1.countableTypes.join('-') === cc2.countableTypes.join('-');
